import React from "react";
import {
  Workflow as WorkflowIcon, ArrowLeft, Plus, Pencil, Eye, RotateCcw,
  Trash2, CheckCircle2, Loader2, Layers,
} from "lucide-react";
import WorkflowEditor from "../features/workflow/WorkflowEditor";
import * as api from "../api/workflows";

/**
 * Admin > Workflows.
 *
 * Routing used to be a Python tuple. This screen is where a customer sees the
 * route their work actually takes, changes it, decides who owns each step, and
 * publishes it — with the published version becoming the authority the rest of
 * the app routes by.
 */

const DOMAIN_BLURB = {
  IDEA: "How an idea travels from submission to an approved demand.",
  DEMAND: "The governed pipeline every demand follows, gate by gate.",
  FULFILMENT: "How approved work is delivered, tested and closed out.",
};

const card = {
  border: "1px solid var(--cares-line-card)", borderRadius: 14, background: "var(--cares-surface-card)",
  boxShadow: "0 1px 3px rgba(15,23,42,.05)",
};
const btn = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px",
  borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: "pointer",
  border: "1px solid var(--cares-line-strong)", background: "var(--cares-surface-card)", color: "var(--cares-text-2)",
};
const btnPrimary = { ...btn, background: "var(--cares-purple)", borderColor: "var(--cares-purple)", color: "var(--cares-text-max)" };

const STATUS_TONE = {
  PUBLISHED: { bg: "color-mix(in srgb, var(--cares-green) 22%, var(--cares-surface-card))", fg: "var(--cares-green)" },
  DRAFT: { bg: "color-mix(in srgb, var(--cares-amber) 22%, var(--cares-surface-card))", fg: "var(--cares-amber)" },
  ARCHIVED: { bg: "var(--cares-surface-board)", fg: "var(--cares-text-label)" },
};

export default function WorkflowAdminPage() {
  const [workflows, setWorkflows] = React.useState([]);
  const [palette, setPalette] = React.useState(null);
  const [actors, setActors] = React.useState([]);
  const [open, setOpen] = React.useState(null);
  const [busy, setBusy] = React.useState("");
  const [error, setError] = React.useState("");
  const [showArchived, setShowArchived] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setError("");
      const [list, pal, act] = await Promise.all([
        api.listWorkflows(), api.getPalette(), api.getActors(),
      ]);
      setWorkflows(list.workflows || []);
      setPalette(pal);
      setActors(act.actors || []);
    } catch (e) {
      setError(e.message || String(e));
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const openWorkflow = async (id, { asDraft } = {}) => {
    setBusy(`open-${id}`); setError("");
    try {
      // Editing a published workflow means editing a draft of it — the published
      // graph is the record of how live work was routed.
      const target = asDraft ? await api.createDraft(id, "mmohen") : { id };
      const full = await api.getWorkflow(target.id);
      setOpen(full);
      await load();
    } catch (e) {
      setError(e.message || String(e));
    } finally { setBusy(""); }
  };

  const create = async () => {
    const name = window.prompt("Name this workflow", "New demand route");
    if (!name) return;
    const domain = window.prompt("Which area? IDEA, DEMAND or FULFILMENT", "DEMAND");
    if (!domain) return;
    setBusy("create");
    try {
      const made = await api.createWorkflow({
        name, domain: domain.toUpperCase(), actor: "mmohen",
      });
      await load();
      openWorkflow(made.id);
    } catch (e) {
      setError(e.message || String(e));
    } finally { setBusy(""); }
  };

  const resetOotb = async () => {
    if (!window.confirm(
      "Reinstall the standard workflows as a new published version?\n\n" +
      "Your existing versions are kept and archived, not deleted.")) return;
    setBusy("seed");
    try { await api.seedOotb(true); await load(); }
    catch (e) { setError(e.message || String(e)); }
    finally { setBusy(""); }
  };

  const discard = async (id) => {
    if (!window.confirm("Discard this draft?")) return;
    try { await api.deleteWorkflow(id); await load(); }
    catch (e) { setError(e.message || String(e)); }
  };

  if (open) {
    return (
      <div>
        <button style={{ ...btn, marginBottom: 12 }} onClick={() => { setOpen(null); load(); }}>
          <ArrowLeft size={13} /> All workflows
        </button>
        <WorkflowEditor
          workflow={open}
          palette={palette}
          actors={actors}
          onSaved={(saved) => setOpen((o) => ({ ...o, ...saved }))}
          onPublished={() => load()}
        />
      </div>
    );
  }

  const byDomain = ["IDEA", "DEMAND", "FULFILMENT"].map((domain) => ({
    domain,
    rows: workflows.filter((w) => w.domain === domain &&
      (showArchived || w.status !== "ARCHIVED")),
  }));

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 16, marginBottom: 20,
                    flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cares-text-1)", margin: 0,
                       display: "flex", alignItems: "center", gap: 9 }}>
            <WorkflowIcon size={20} style={{ color: "var(--cares-purple)" }} /> Workflows
          </h1>
          <p style={{ fontSize: 13, color: "var(--cares-text-label)", margin: "6px 0 0", maxWidth: 620 }}>
            The routes work takes through ideation, demand management and fulfilment.
            The published workflow in each area decides what happens next for every
            record — edit it here and the rest of the application follows.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btn} onClick={resetOotb} disabled={!!busy}>
            {busy === "seed" ? <Loader2 size={13} /> : <RotateCcw size={13} />} Reset to standard
          </button>
          <button style={btnPrimary} onClick={create} disabled={!!busy}>
            <Plus size={13} /> New workflow
          </button>
        </div>
      </div>

      {error && (
        <div style={{ ...card, borderColor: "color-mix(in srgb, var(--cares-orange) 30%, var(--cares-surface-card))", background: "color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))", padding: 12,
                      marginBottom: 16, fontSize: 12.5, color: "var(--cares-orange)" }}>
          {error}
        </div>
      )}

      {byDomain.map(({ domain, rows }) => (
        <div key={domain} style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 9 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: "var(--cares-text-1)", margin: 0 }}>
              {domain === "IDEA" ? "Ideation"
                : domain === "DEMAND" ? "Demand Management" : "Fulfilment"}
            </h2>
            <span style={{ fontSize: 11.5, color: "var(--cares-text-note)" }}>{DOMAIN_BLURB[domain]}</span>
          </div>

          {rows.length === 0 ? (
            <div style={{ ...card, padding: 16, fontSize: 12.5, color: "var(--cares-text-note)" }}>
              No workflow here yet — built-in routing applies.
            </div>
          ) : rows.map((w) => {
            const tone = STATUS_TONE[w.status] || STATUS_TONE.ARCHIVED;
            return (
              <div key={w.id} style={{ ...card, padding: 14, marginBottom: 8 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--cares-text-1)" }}>
                    {w.name}
                  </span>
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px",
                                 borderRadius: 999, background: tone.bg, color: tone.fg }}>
                    {w.status} · v{w.version}
                  </span>
                  {w.isOotb && (
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px",
                                   borderRadius: 999, background: "color-mix(in srgb, var(--cares-purple) 14%, var(--cares-surface-card))", color: "var(--cares-purple)" }}>
                      standard
                    </span>
                  )}
                  {w.status === "PUBLISHED" && (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4,
                                   fontSize: 11, color: "var(--cares-green)" }}>
                      <CheckCircle2 size={12} /> routing {domain.toLowerCase()} work now
                    </span>
                  )}

                  <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                    {w.status === "DRAFT" ? (
                      <>
                        <button style={btnPrimary} onClick={() => openWorkflow(w.id)}
                                disabled={!!busy}>
                          <Pencil size={12} /> Continue editing
                        </button>
                        <button style={btn} onClick={() => discard(w.id)}>
                          <Trash2 size={12} />
                        </button>
                      </>
                    ) : w.status === "PUBLISHED" ? (
                      <>
                        <button style={btn} onClick={() => openWorkflow(w.id)} disabled={!!busy}>
                          <Eye size={12} /> View
                        </button>
                        <button style={btnPrimary} disabled={!!busy}
                                onClick={() => openWorkflow(w.id, { asDraft: true })}>
                          {busy === `open-${w.id}` ? <Loader2 size={12} /> : <Pencil size={12} />}
                          Edit
                        </button>
                      </>
                    ) : (
                      <button style={btn} onClick={() => openWorkflow(w.id)} disabled={!!busy}>
                        <Eye size={12} /> View
                      </button>
                    )}
                  </div>
                </div>

                {w.description && (
                  <p style={{ fontSize: 12, color: "var(--cares-text-label)", margin: "7px 0 0" }}>
                    {w.description}
                  </p>
                )}

                {/* The route at a glance, so the list is readable without opening
                    the canvas. */}
                {w.stageSequence?.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 9,
                                flexWrap: "wrap" }}>
                    <Layers size={12} style={{ color: "var(--cares-text-dim)" }} />
                    {w.stageSequence.map((s, i) => (
                      <React.Fragment key={s}>
                        {i > 0 && <span style={{ color: "var(--cares-text-dim)", fontSize: 10 }}>→</span>}
                        <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px",
                                       borderRadius: 5, background: "var(--cares-surface-board)", color: "var(--cares-text-3)" }}>
                          {s}
                        </span>
                      </React.Fragment>
                    ))}
                    <span style={{ fontSize: 10.5, color: "var(--cares-text-dim)", marginLeft: 4 }}>
                      {w.nodeCount} steps · {w.edgeCount} routes
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12,
                      color: "var(--cares-text-label)", cursor: "pointer" }}>
        <input type="checkbox" checked={showArchived}
               onChange={(e) => setShowArchived(e.target.checked)} />
        Show archived versions — kept as the record of how work used to be routed
      </label>
    </div>
  );
}
